import type { StoreApi } from 'zustand'
import type { AppState } from '@/store/rootTypes'
import { generateLearningReport } from '@/entities/report'
import { createLearningPlanKey } from '@/store/learningPlan'
import { addArchivedCount, appendWithRetention, DATA_RETENTION_LIMITS } from '@/store/retention'
import { activeWorkspacePatch } from '@/store/workspace'
import { awardEvidenceAchievements } from '@/store/achievementProjection'
import { isQualifiedEvidence, type ExerciseAttempt, type LearningEvidenceKind } from '@/entities/attempt'
import type { WrongQuestion } from '@/types'

type SetState = StoreApi<AppState>['setState']

function inferEvidenceKind(attempt: ExerciseAttempt): LearningEvidenceKind {
  if (attempt.evidenceKind) return attempt.evidenceKind
  return attempt.source === 'assessment' ? 'summative' : 'formative'
}

function normalizeLearningAttempt(state: AppState, attempt: ExerciseAttempt): ExerciseAttempt {
  return {
    ...attempt,
    stageId: attempt.stageId ?? state.currentStage?.id,
    evidenceKind: inferEvidenceKind(attempt),
  }
}

export function resolveLearningAttemptReplay(
  state: AppState,
  attempt: ExerciseAttempt,
  owner: string,
): { status: 'new' | 'duplicate'; attempt: ExerciseAttempt } {
  const normalizedAttempt = normalizeLearningAttempt(state, attempt)
  const existingAttempt = state.attempts.find((item) => item.id === normalizedAttempt.id)
  if (!existingAttempt) return { status: 'new', attempt: normalizedAttempt }
  if (JSON.stringify(existingAttempt) !== JSON.stringify(normalizedAttempt)) {
    throw new Error(`${owner} attempt ${attempt.id} was replayed with different evidence.`)
  }
  return { status: 'duplicate', attempt: normalizedAttempt }
}

function isSameWrongCase(question: WrongQuestion, attempt: ExerciseAttempt): boolean {
  return question.studentId === attempt.studentId
    && question.learningStageId === attempt.stageId
    && question.unitId === attempt.unitId
    && question.originalQuestionId === attempt.exerciseId
}

function projectFormativeWrongCase(
  wrongQuestions: readonly WrongQuestion[],
  attempt: ExerciseAttempt,
): WrongQuestion[] {
  if (!isQualifiedEvidence(attempt)
    || attempt.evidenceKind !== 'formative'
    || attempt.source === 'assessment'
    || attempt.source === 'ai-teacher'
    || !attempt.unitId) {
    return [...wrongQuestions]
  }
  const existingIndex = wrongQuestions.findIndex((question) => isSameWrongCase(question, attempt))
  if (existingIndex < 0 && attempt.correct) return [...wrongQuestions]

  const variantAttempt = {
    attemptId: attempt.id,
    correct: attempt.correct,
    score: attempt.score,
    completedAt: attempt.completedAt,
  }
  if (existingIndex >= 0) {
    const existing = wrongQuestions[existingIndex]
    if (existing.variantAttempts.some((entry) => entry.attemptId === attempt.id)) return [...wrongQuestions]
    const updated: WrongQuestion = {
      ...existing,
      masteryState: attempt.correct
        ? ((existing.masteryState ?? existing.status) === 'mastered' ? 'mastered' : 'corrected')
        : 'wrong',
      status: attempt.correct
        ? ((existing.masteryState ?? existing.status) === 'mastered' ? 'mastered' : 'corrected')
        : 'wrong',
      questionSnapshot: existing.questionSnapshot ?? attempt.questionSnapshot,
      replayDescriptor: existing.replayDescriptor ?? attempt.replayDescriptor,
      variantAttempts: [...existing.variantAttempts, variantAttempt],
    }
    return wrongQuestions.map((question, index) => index === existingIndex ? updated : question)
  }

  return [...wrongQuestions, {
    id: `wq:${attempt.studentId}:${attempt.stageId ?? 'legacy'}:${attempt.unitId}:${attempt.exerciseId}`,
    studentId: attempt.studentId,
    originalQuestionId: attempt.exerciseId,
    unitId: attempt.unitId,
    category: attempt.category ?? '学习练习',
    ability: attempt.ability,
    knowledgePointId: attempt.knowledgePointId,
    source: attempt.source as WrongQuestion['source'],
    learningStageId: attempt.stageId,
    studentAnswer: attempt.studentAnswer ?? '',
    correctAnswer: attempt.correctAnswer ?? '',
    questionSnapshot: attempt.questionSnapshot,
    replayDescriptor: attempt.replayDescriptor,
    aiExplanation: '这道题第一次作答有误。普通重做正确只表示已订正，通过独立认证后才算掌握。',
    variantAttempts: [variantAttempt],
    remediationLoopCount: 0,
    masteryState: 'wrong',
    workflowState: 'idle',
    status: 'wrong',
    createdAt: new Date(attempt.completedAt),
  }]
}

export function projectLearningAttemptPatch(state: AppState, attempt: ExerciseAttempt) {
  const stagedAttempt = normalizeLearningAttempt(state, attempt)
  const retained = appendWithRetention(state.attempts, stagedAttempt, DATA_RETENTION_LIMITS.attempts)
  const attempts = retained.items
  const achievementProjection = awardEvidenceAchievements(state.game, attempts)
  const projectedWrongQuestions = projectFormativeWrongCase(state.wrongQuestions, stagedAttempt)
  const retainedWrongQuestions = projectedWrongQuestions.length > DATA_RETENTION_LIMITS.wrongQuestions
    ? {
        items: projectedWrongQuestions.slice(-DATA_RETENTION_LIMITS.wrongQuestions),
        archivedCount: projectedWrongQuestions.length - DATA_RETENTION_LIMITS.wrongQuestions,
      }
    : { items: projectedWrongQuestions, archivedCount: 0 }
  let archiveStats = addArchivedCount(state.archiveStats, 'attempts', retained.archivedCount)
  archiveStats = addArchivedCount(archiveStats, 'wrongQuestions', retainedWrongQuestions.archivedCount)
  const workspacePatch = {
    attempts,
    game: achievementProjection.game,
    wrongQuestions: retainedWrongQuestions.items,
    currentReport: generateLearningReport(attempts, stagedAttempt.studentId, {
      stageId: stagedAttempt.stageId,
      assessmentRecords: state.assessmentRecords,
    }),
    archiveStats,
    weeklyPlanActive: true,
    weeklyPlanStartDate: state.weeklyPlanStartDate ?? stagedAttempt.completedAt,
  }
  return {
    workspacePatch,
    pendingAchievements: [
      ...new Set([...state.pendingAchievements, ...achievementProjection.unlockedIds]),
    ],
  }
}

export function createLearningSlice(set: SetState): Pick<AppState,
  'setCurrentPlan' | 'clearCurrentPlan' | 'addAttempt' | 'clearAttempts' |
  'regenerateReport' | 'addLearningEvent' | 'clearLearningEvents'> {
  return {
    setCurrentPlan: (plan, source) => set((state) => {
      const patch = { currentPlan: plan, currentPlanKey: createLearningPlanKey(source) }
      return { ...patch, ...activeWorkspacePatch(state, patch) }
    }),
    clearCurrentPlan: () => set((state) => {
      const patch = { currentPlan: null, currentPlanKey: null }
      return { ...patch, ...activeWorkspacePatch(state, patch) }
    }),
    addAttempt: (attempt) => set((state) => {
      const projection = projectLearningAttemptPatch(state, attempt)
      return {
        ...projection.workspacePatch,
        pendingAchievements: projection.pendingAchievements,
        ...activeWorkspacePatch(state, projection.workspacePatch),
      }
    }),
    clearAttempts: () => set((state) => {
      const patch = { attempts: [], currentReport: null }
      return { ...patch, ...activeWorkspacePatch(state, patch) }
    }),
    regenerateReport: (studentId) => set((state) => {
      const patch = {
        currentReport: generateLearningReport(state.attempts, studentId, {
          stageId: state.currentStage?.id,
          assessmentRecords: state.assessmentRecords,
        }),
      }
      return { ...patch, ...activeWorkspacePatch(state, patch) }
    }),
    addLearningEvent: (event) => set((state) => {
      const retained = appendWithRetention(state.learningEvents, event, DATA_RETENTION_LIMITS.learningEvents)
      const archiveStats = addArchivedCount(state.archiveStats, 'learningEvents', retained.archivedCount)
      const patch = { learningEvents: retained.items, archiveStats }
      if (!state.activeStudentId || !state.workspaces[state.activeStudentId]) return patch
      return {
        ...patch,
        workspaces: {
          ...state.workspaces,
          [state.activeStudentId]: { ...state.workspaces[state.activeStudentId], ...patch },
        },
      }
    }),
    clearLearningEvents: () => set((state) => {
      const patch = { learningEvents: [] }
      return { ...patch, ...activeWorkspacePatch(state, patch) }
    }),
  }
}
